import { CalendarOutlined, RetweetOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { TimesheetStatusEnum } from '@nxsys/constants/AppEnums';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import {
  NewDraftPayroll,
  TimeSheetDataItem,
} from '@nxsys/types/models/timesheet';
import {
  calcGrossPay,
  formatStatus,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tag, Tooltip, Typography } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { find, includes, isEmpty, toNumber } from 'lodash';
import moment from 'moment';
import { DefaultTheme } from 'styled-components';

const getActionColumn = ({
  messages,
  handleCancel,
  handleOpenReverse,
  handleView,
  handleChangeTaxPeriod,
  theme,
  hideCancel = false,
  hideReverse = false,
  width = 150,
}: {
  messages: TypedMessages;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleOpenReverse?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  handleChangeTaxPeriod?: (list: TimeSheetDataItem[]) => void;
  theme: DefaultTheme;
  hideCancel?: boolean;
  hideReverse?: boolean;
  width?: number;
}): ColumnType<TimeSheetDataItem> => ({
  key: 'action',
  title: messages['common.action'],
  fixed: 'left',
  align: 'left',
  render: (record) => {
    const ableCancelTs = !(
      record?.approveStatus === TimesheetStatusEnum.CANCEL ||
      record?.newDraftPayroll ||
      record?.newPayroll
    );
    const ableReverseTs = record?.newDraftPayroll || record?.newPayroll;

    return (
      <ul className="flex items-center justify-start gap-x-[8px]">
        <li onClick={() => handleView?.(record.id)}>
          <Tooltip title={messages['common.edit']}>
            <span>
              <Icons.IconEdit />
            </span>
          </Tooltip>
        </li>
        {record.file && (
          <li onClick={() => window.open(record.file, '_blank')}>
            <Tooltip title={messages['common.download']}>
              <span className="w-[20px] h-[20px]">
                <Icons.IconDownload />
              </span>
            </Tooltip>
          </li>
        )}
        {!hideCancel && ableCancelTs && (
          <li onClick={() => handleCancel?.([record])}>
            <Tooltip title={messages['timesheet.cancel']}>
              <span>
                <Icons.IconClose color={theme.palette.red[500]} />
              </span>
            </Tooltip>
          </li>
        )}
        {ableCancelTs && handleChangeTaxPeriod && (
          <li onClick={() => handleChangeTaxPeriod?.([record])}>
            <Tooltip title={messages['timesheet.changeTaxPeriod']}>
              <span>
                <CalendarOutlined
                  style={{ color: theme.palette.primary.main }}
                />
              </span>
            </Tooltip>
          </li>
        )}
        {!hideReverse && ableReverseTs && (
          <li onClick={() => handleOpenReverse?.([record])}>
            <Tooltip title={messages['timesheet.reverse']}>
              <span>
                <RetweetOutlined style={{ color: theme.palette.orange[300] }} />
              </span>
            </Tooltip>
          </li>
        )}
        {record?.isPAL && (
          <li>
            <Tag color="warning" className="ml-1">
              PAL
            </Tag>
          </li>
        )}
      </ul>
    );
  },
  width,
});

export const getColumns = ({
  messages,
  payrollCountry,
  handleCancel,
  handleOpenReverse,
  handleView,
  handleChangeTaxPeriod,
  theme,
  isMobile,
  isAwaitingInvoice = false,
  isBureau = false,
  isAgency = false,
  hideReverse = false,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleOpenReverse?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  handleChangeTaxPeriod?: (list: TimeSheetDataItem[]) => void;
  theme: DefaultTheme;
  isMobile: boolean;
  isAwaitingInvoice?: boolean;
  isBureau: boolean;
  isAgency?: boolean;
  hideReverse?: boolean;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    getActionColumn({
      messages,
      handleCancel,
      handleOpenReverse,
      handleView,
      handleChangeTaxPeriod,
      theme,
      hideReverse,
    }),
    ...((!isAwaitingInvoice
      ? [
          {
            key: 'status',
            title: messages['common.status'],
            align: 'center',
            ...(!isMobile && { fixed: 'left' }),
            dataIndex: 'status',
            render: (value) => (
              <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
                {value}
              </AppTagStatus>
            ),
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    // NX-5237 (#8): agency-only column surfacing the approval-stage tax-period
    // change reason (mirrors the reject "Notes from Company" precedent).
    // Andy 10/09: keep it directly after Status. Out past the money columns it
    // was "not instinctively visible" and nobody knew a reason was recorded.
    ...((isAgency
      ? [
          {
            key: 'updateReason',
            title: messages['timesheet.updateReason'],
            dataIndex: 'updateReason',
            width: 300,
            render: (value: string) => value || '_',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      render: (value) =>
        value?.subcontractorRef
          ? `${value?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
              value?.subcontractorRef?.agencyRef || '___'
            }`
          : value?.starter
            ? value?.starter.agencyRef
            : '___',
      width: 250,
    },
    ...((!isAwaitingInvoice && isBureau
      ? [
          {
            key: 'employeeId',
            title: messages['common.employeeId'],
            dataIndex: 'subcontractorRef',
            render: (value) => value?.id ?? '',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    ...((!isAwaitingInvoice && !isBureau
      ? [
          {
            key: 'assignmentName',
            title: messages['assignment.detail.assignmentName'],
            dataIndex: 'assignmentRef',
            render: (value) => value?.assignmentName || '_',
            width: 240,
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    ...((isAwaitingInvoice
      ? [
          {
            key: 'timesheetId',
            title: messages['timesheet.timesheetId'],
            dataIndex: 'id',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
          },
          {
            key: 'employeeId',
            title: messages['common.systemId'],
            dataIndex: 'subcontractorRef',
            render: (value) => value?.id ?? '',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'name',
      title: messages['common.name'],
      width: 240,
      render: (value) =>
        value?.subcontractorRef
          ? value?.subcontractorRef?.fullName ||
            `${value?.subcontractorRef?.forename || '_'} ${
              value?.subcontractorRef?.surname || '_'
            }`
          : value?.starter
            ? value?.starter?.fullName ||
              `${value?.starter?.forename || '_'} ${
                value?.starter?.surname || '_'
              }`
            : '_',
    },
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (value) => value?.batch || '_',
    },
    ...((isAwaitingInvoice
      ? [
          {
            key: 'paymentCompany',
            title: isBureau
              ? messages['common.employer']
              : messages['common.paymentCompany'],
            dataIndex: 'companyRef',
            render: (value) => value?.companyName ?? '',
          },
          ...(!isBureau
            ? [
                {
                  key: 'agencyName',
                  title: messages['common.agencyName'],
                  dataIndex: 'agencyRef',
                  render: (value: any) => value?.name ?? '',
                },
              ]
            : []),
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(
          value?.totalNet || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(
          value?.totalVat || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(
          value?.totalPrices || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    ...((!isAwaitingInvoice
      ? [
          {
            key: 'receipt',
            title: messages['timesheet.receipt'],
            align: 'right',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (value) =>
              formatMoney(
                value?.receipt || 0,
                showCurrencyByCountry(payrollCountry),
              ),
          },
          {
            key: 'outstanding',
            title: messages['common.outstanding'],
            align: 'right',
            render: (value) =>
              formatMoney(
                value?.outstanding || 0,
                showCurrencyByCountry(payrollCountry),
              ),
          },
          ...(!isBureau
            ? [
                {
                  key: 'invoiceNo',
                  title: messages['timesheet.salesInvRef'],
                  dataIndex: 'invoiceNo',
                },
              ]
            : []),
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
  ];
};

export const getAwaitingInvoiceColumns = ({
  messages,
  payrollCountry,
  handleCancel,
  handleOpenReverse,
  handleView,
  theme,
  isBureau = false,
  hideReverse = false,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleOpenReverse?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  theme: DefaultTheme;
  isBureau?: boolean;
  hideReverse?: boolean;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    getActionColumn({
      messages,
      handleCancel,
      handleOpenReverse,
      handleView,
      theme,
      hideReverse,
    }),
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      render: (value) =>
        value?.subcontractorRef
          ? `${value?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
              value?.subcontractorRef?.agencyRef || '___'
            }`
          : value?.starter
            ? value?.starter.agencyRef
            : '___',
      width: 250,
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      dataIndex: 'id',
    },
    {
      key: 'employeeId',
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id ?? '',
    },
    {
      key: 'name',
      title: messages['common.subcontractorName'],
      width: 240,
      render: (value) =>
        value?.subcontractorRef
          ? value?.subcontractorRef?.fullName ||
            `${value?.subcontractorRef?.forename || '_'} ${
              value?.subcontractorRef?.surname || '_'
            }`
          : value?.starter
            ? value?.starter?.fullName ||
              `${value?.starter?.forename || '_'} ${
                value?.starter?.surname || '_'
              }`
            : '_',
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (value) => value?.batch || '_',
    },
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      dataIndex: 'companyRef',
      render: (value) => value?.companyName ?? '',
    },
    ...((!isBureau
      ? [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'agencyRef',
            render: (value) => value?.name ?? '',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      align: 'right',
      width: 200,
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalNet || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalVat || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalPrices || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];
};

export const getPendingAgencyApprovalColumns = ({
  messages,
  payrollCountry,
  handleCancel,
  handleOpenReverse,
  handleView,
  handleChangeTaxPeriod,
  theme,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleOpenReverse?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  handleChangeTaxPeriod?: (list: TimeSheetDataItem[]) => void;
  theme: DefaultTheme;
  isBureau: boolean;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    getActionColumn({
      messages,
      handleCancel,
      handleOpenReverse,
      handleView,
      handleChangeTaxPeriod,
      theme,
      hideReverse: true,
      width: 120,
    }),
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      render: (value) =>
        value?.subcontractorRef
          ? `${value?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
              value?.subcontractorRef?.agencyRef || '___'
            }`
          : value?.starter
            ? value?.starter.agencyRef
            : '___',
      width: 250,
    },
    // NX-5237 (#9): a tax-period change sends the timesheet back here for
    // re-approval — surface the captured reason so the approver knows why.
    // Andy 10/09: it belongs at the start of the row, not past the money columns.
    {
      key: 'updateReason',
      title: messages['timesheet.updateReason'],
      dataIndex: 'updateReason',
      width: 300,
      render: (value: string) => value || '_',
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      dataIndex: 'id',
    },
    {
      key: 'employeeId',
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id ?? '',
    },
    {
      key: 'name',
      title: messages['common.candidateName'],
      width: 240,
      render: (value) =>
        value?.subcontractorRef
          ? value?.subcontractorRef?.fullName ||
            `${value?.subcontractorRef?.forename || '_'} ${
              value?.subcontractorRef?.surname || '_'
            }`
          : value?.starter
            ? value?.starter?.fullName ||
              `${value?.starter?.forename || '_'} ${
                value?.starter?.surname || '_'
              }`
            : '_',
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (value) => value?.batch || '_',
    },
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      dataIndex: 'companyRef',
      render: (value) => value?.companyName ?? '',
    },
    ...((!isBureau
      ? [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'agencyRef',
            render: (value) => value?.name ?? '',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      width: 200,
      align: 'right',
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalNet || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalVat || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalPrices || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];
};

export const getGenerateDraftPayrollColumns = ({
  messages,
  payrollCountry,
  handleCancel,
  handleCancelSalary,
  handleView,
  handleChangeTaxPeriod,
  theme,
  errTimesheets = [],
  errorPreIds = [],
  errorBefIds = [],
  errorPreSubIds = [],
  errorBefSubIds = [],
  aepnSubmittedSubIds = [],
  isBureau = false,
  onNmwBreakdownClick,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleCancelSalary?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  handleChangeTaxPeriod?: (list: TimeSheetDataItem[]) => void;
  theme: DefaultTheme;
  errTimesheets: TimeSheetDataItem[];
  errorPreIds: string[];
  errorBefIds: string[];
  errorPreSubIds?: string[];
  errorBefSubIds?: string[];
  aepnSubmittedSubIds?: string[];
  isBureau?: boolean;
  onNmwBreakdownClick?: (data: NewDraftPayroll) => void;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => {
        const ableCancelTs = !(
          record?.approveStatus === TimesheetStatusEnum.CANCEL ||
          record?.newDraftPayroll ||
          record?.newPayroll
        );

        return (
          <ul className="flex items-center justify-start gap-x-[8px]">
            {!record?.isSalary && (
              <li onClick={() => handleView?.(record.id)}>
                <Tooltip title={messages['common.edit']}>
                  <span>
                    <Icons.IconEdit />
                  </span>
                </Tooltip>
              </li>
            )}
            {record.file && !record?.isSalary && (
              <li onClick={() => window.open(record.file, '_blank')}>
                <Tooltip title={messages['common.download']}>
                  <span className="w-[20px] h-[20px]">
                    <Icons.IconDownload />
                  </span>
                </Tooltip>
              </li>
            )}
            {!record?.isSalary && ableCancelTs && (
              <li onClick={() => handleCancel?.([record])}>
                <Tooltip title={messages['timesheet.cancel']}>
                  <span>
                    <Icons.IconClose color={theme.palette.red[500]} />
                  </span>
                </Tooltip>
              </li>
            )}
            {record?.isSalary && (
              <li onClick={() => handleCancelSalary?.([record])}>
                <Tooltip title={messages['timesheet.cancel']}>
                  <span>
                    <Icons.IconClose color={theme.palette.red[500]} />
                  </span>
                </Tooltip>
              </li>
            )}
            {!record?.isSalary && ableCancelTs && handleChangeTaxPeriod && (
              <li onClick={() => handleChangeTaxPeriod?.([record])}>
                <Tooltip title={messages['timesheet.changeTaxPeriod']}>
                  <span>
                    <CalendarOutlined
                      style={{ color: theme.palette.primary.main }}
                    />
                  </span>
                </Tooltip>
              </li>
            )}
            {record?.isPAL && (
              <li>
                <Tag color="warning" className="ml-1">
                  PAL
                </Tag>
              </li>
            )}
          </ul>
        );
      },
      width: 150,
    },
    ...((isBureau
      ? [
          {
            key: 'employerRef',
            title: messages['common.employerRef'] ?? 'Employer Ref',
            render: (record: TimeSheetDataItem) =>
              record?.isSalary
                ? record?.companyPayment?.id || '_'
                : record?.companyRef?.id || '_',
            width: 150,
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      render: (record) => (record?.isSalary ? '_' : record?.id || '_'),
    },
    {
      key: 'id',
      title: messages['common.systemId'],
      render: (record) =>
        record?.isSalary ? record?.id : record?.subcontractorRef?.id || '_',
      width: 150,
    },
    {
      key: 'name',
      title: messages['common.employeeName'],
      width: 240,
      render: (record) =>
        record?.isSalary
          ? record?.fullName
          : record?.subcontractorRef?.fullName ||
            `${record?.subcontractorRef?.forename || '_'} ${
              record?.subcontractorRef?.surname || '_'
            }`,
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (record) => record?.batch || '_',
    },
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      render: (record) =>
        record?.companyRef?.companyName ||
        record?.companyPayment?.companyName ||
        '_',
      width: 200,
    },
    ...((isBureau
      ? []
      : [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'agencyRef',
            render: (value: any) => value?.name ?? '',
          },
        ]) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'grossPay',
      title: messages['common.grossPay'] ?? 'Gross Pay',
      width: 200,
      align: 'right',
      render: (record) => {
        if (record?.isSalary) {
          return record?.grossPay
            ? formatMoney(
                record.grossPay,
                showCurrencyByCountry(payrollCountry),
              )
            : formatMoney(
                calcGrossPay(
                  record?.annualSalary || 0,
                  record?.payFrequency ?? '',
                ) || 0,
                showCurrencyByCountry(payrollCountry),
              );
        }
        return formatMoney(
          record?.totalNet || 0,
          showCurrencyByCountry(payrollCountry),
        );
      },
    },
    ...((isBureau
      ? []
      : [
          {
            key: 'totalVat',
            title: messages['common.vat'],
            align: 'right' as const,
            render: (value: TimeSheetDataItem) =>
              formatMoney(
                toNumber(value?.totalVat || 0),
                showCurrencyByCountry(payrollCountry),
              ),
          },
          {
            key: 'totalPrices',
            title: messages['common.total'],
            align: 'right' as const,
            render: (value: TimeSheetDataItem) =>
              formatMoney(
                toNumber(value?.totalPrices || 0),
                showCurrencyByCountry(payrollCountry),
              ),
          },
        ]) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      width: 200,
      align: 'right',
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'error',
      title: 'Error message',
      width: 350,
      render: (record) => {
        const { id } = record;
        const errors = record?.newDraftPayroll?.errors || [];
        const invalidTs = find(errTimesheets, (its) => its?.id === id);

        return (
          <Typography.Text type="danger">
            {!isEmpty(errors) ? (
              <span>
                {errors?.[0]?.message || ''}
                {errors?.[0]?.errorType ? (
                  <>
                    {messages['common.toViewABreakdown']}
                    <AppButton
                      className="!px-1"
                      name={messages['common.clickHere']}
                      type="link"
                      onClick={() =>
                        onNmwBreakdownClick?.(record.newDraftPayroll!)
                      }
                    />
                  </>
                ) : null}
              </span>
            ) : includes(errorPreIds, id) && includes(errorBefIds, id) ? (
              messages['timesheet.generateDraftPayroll.error.preAndBef']
            ) : includes(errorPreSubIds, id) && includes(errorBefSubIds, id) ? (
              messages['timesheet.generateDraftPayroll.error.preAndBef']
            ) : includes(errorPreIds, id) || includes(errorPreSubIds, id) ? (
              messages['timesheet.generateDraftPayroll.error.preOnly']
            ) : includes(errorBefIds, id) || includes(errorBefSubIds, id) ? (
              messages['timesheet.generateDraftPayroll.error.befOnly']
            ) : includes(aepnSubmittedSubIds, id) ||
              includes(aepnSubmittedSubIds, record?.subcontractorRef?.id) ? (
              messages['timesheet.generateDraftPayroll.error.aepnSubmitted']
            ) : invalidTs ? (
              invalidTs?.message ||
              messages['timesheet.generateDraftPayroll.error.invalidTimesheet']
            ) : null}
          </Typography.Text>
        );
      },
    },
  ];
};

export const getSelectPayrollColumns = ({
  messages,
  payrollCountry,
  handleView,
  theme,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleView?: (id: string) => void;
  theme: DefaultTheme;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    getActionColumn({
      messages,
      handleView,
      theme,
      hideCancel: true,
      hideReverse: true,
      width: 100,
    }),
    {
      key: 'employerRef',
      title: messages['common.employerRef'] ?? 'Employer Ref',
      render: (record) =>
        record?.isSalary
          ? record?.companyPayment?.id || '_'
          : record?.companyRef?.id || '_',
      width: 150,
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      render: (record) => (record?.isSalary ? '_' : record?.id || '_'),
      width: 150,
    },
    {
      key: 'id',
      title: messages['common.systemId'],
      render: (record) =>
        record?.isSalary ? record?.id : record?.subcontractorRef?.id || '_',
      width: 150,
    },
    {
      key: 'employeeName',
      title: messages['common.employeeName'],
      width: 280,
      render: (record) =>
        record?.isSalary
          ? record?.fullName
          : record?.subcontractorRef?.fullName ||
            `${record?.subcontractorRef?.forename || '_'} ${record?.subcontractorRef?.surname || '_'}`,
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (record) => record?.batch || '_',
      width: 150,
    },
    {
      key: 'employer',
      title: messages['common.employer'],
      render: (record) =>
        record?.companyRef?.companyName ||
        record?.companyPayment?.companyName ||
        '_',
      width: 250,
    },
    {
      key: 'grossPay',
      title: messages['common.grossPay'] ?? 'Gross Pay',
      width: 200,
      align: 'right',
      render: (record) => {
        if (record?.grossPay) {
          return formatMoney(
            record.grossPay,
            showCurrencyByCountry(payrollCountry),
          );
        }
        if (record?.annualSalary) {
          return formatMoney(
            calcGrossPay(record.annualSalary, record?.payFrequency ?? '') || 0,
            showCurrencyByCountry(payrollCountry),
          );
        }
        return '_';
      },
    },
    {
      key: 'timesheetValue',
      title: messages['common.timesheetValue'] ?? 'Timesheet Value',
      width: 200,
      align: 'right',
      render: (record) =>
        record?.totalNet
          ? formatMoney(record.totalNet, showCurrencyByCountry(payrollCountry))
          : '_',
    },
  ];
};

export const getDraftPayrollErrorColumns = ({
  messages,
  onDelete,
  theme,
  isBureau = false,
  onNmwBreakdownClick,
}: {
  messages: TypedMessages;
  onDelete: (id: string) => void;
  theme: DefaultTheme;
  isBureau?: boolean;
  onNmwBreakdownClick?: (data: DraftPayroll) => void;
}): ColumnsType<DraftPayroll> => {
  return [
    {
      key: 'actions',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => onDelete?.(record.id)}>
            <Tooltip title={messages['common.delete']}>
              <span>
                <Icons.IconDelete color={theme.palette.red[500]} />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'employeeID',
      title: messages['common.employeeId'],
      width: 150,
      render: (record) => record?.subcontractorRef?.id ?? '',
    },
    {
      key: 'employeeName',
      title: messages['common.employeeName'],
      render: (record) =>
        record?.subcontractorRef?.fullName ??
        `${record?.subcontractorRef?.forename ?? ''} ${
          record?.subcontractorRef?.surname ?? ''
        }`,
      width: 350,
    },
    ...((!isBureau
      ? [
          {
            key: 'agencyRef',
            title: messages['common.agencyRef'],
            width: 250,
            render: (record) =>
              `${record?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
                record?.subcontractorRef?.agencyRef || ''
              }`,
          },
        ]
      : []) as ColumnsType<DraftPayroll>),
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      width: 350,
      render: (record) => record?.companyRef?.companyName ?? '',
    },
    {
      key: 'payFrequency',
      title: messages['common.payFrequency'],
      render: (record) => record?.payFrequency ?? '',
      width: 200,
    },
    ...((!isBureau
      ? [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            width: 350,
            render: (record) => record?.agencyRef?.name ?? '',
          },
        ]
      : []) as ColumnsType<DraftPayroll>),
    {
      key: 'errorType',
      title: messages['common.errorType'],
      dataIndex: 'errors',
      render: (errors) => errors?.[0]?.type ?? '',
      width: 150,
    },
    {
      key: 'error',
      title: messages['common.errorMessage'],
      width: 350,
      render: (record) => {
        const errors = record.errors;
        return (
          <Typography.Text type="danger">
            {errors?.[0]?.message || ''}
            {errors?.[0]?.errorType ? (
              <>
                {messages['common.toViewABreakdown']}
                <AppButton
                  className="!px-1"
                  name={messages['common.clickHere']}
                  type="link"
                  onClick={() => onNmwBreakdownClick?.(record)}
                />
              </>
            ) : null}
          </Typography.Text>
        );
      },
    },
  ];
};

export const getTimesheetAgencyColumns = ({
  messages,
  payrollCountry,
  handleView,
  handleApprove,
  handleReject,
  isMobile,
  theme,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleView: (id: string) => void;
  handleApprove: (record: TimeSheetDataItem) => void;
  handleReject: (record: TimeSheetDataItem) => void;
  isMobile: boolean;
  theme: DefaultTheme;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    {
      key: 'actions',
      title: messages['common.action'],
      fixed: 'left',
      width: 100,
      align: 'left',
      render: (record) => {
        return (
          <ul className="flex items-center justify-start gap-x-[8px]">
            <li onClick={() => handleView(record?.id)}>
              <Tooltip title={messages['common.edit']}>
                <span>
                  <Icons.IconEdit />
                </span>
              </Tooltip>
            </li>
            {record?.approveStatus ===
              TimesheetStatusEnum.AWAITING_APPROVAL && (
              <>
                <li onClick={() => handleApprove(record)}>
                  <Tooltip title={messages['common.approve']}>
                    <span>
                      <Icons.IconCheckCircle />
                    </span>
                  </Tooltip>
                </li>
                <li onClick={() => handleReject(record)}>
                  <Tooltip title={messages['common.reject']}>
                    <span>
                      <Icons.IconCloseCircle color={theme.palette.red[500]} />
                    </span>
                  </Tooltip>
                </li>
              </>
            )}
          </ul>
        );
      },
    },
    {
      key: 'status',
      title: messages['common.status'],
      ...(!isMobile && { fixed: 'left' }),
      dataIndex: 'status',
      render: (value) => (
        <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
          {value}
        </AppTagStatus>
      ),
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      dataIndex: 'id',
    },
    {
      key: 'employeeId',
      title: messages['table.subcontractorId'],
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id ?? '',
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (value) => value?.batch || '_',
    },
    {
      key: 'invoicedDate',
      title: messages['common.date'],
      width: 200,
      align: 'right',
      render: (value) =>
        value?.invoicedDate
          ? moment(value.invoicedDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'name',
      title: messages['common.name'],
      width: 240,
      render: (value) =>
        value?.subcontractorRef
          ? value?.subcontractorRef?.fullName ||
            `${value?.subcontractorRef?.forename || '_'} ${
              value?.subcontractorRef?.surname || '_'
            }`
          : value?.starter
            ? value?.starter?.fullName ||
              `${value?.starter?.forename || '_'} ${
                value?.starter?.surname || '_'
              }`
            : '_',
    },
    {
      key: 'startDate',
      title: messages['timesheet.weekStartDate'],
      width: 200,
      align: 'right',
      render: (value) =>
        value?.startDate
          ? moment(value.startDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      width: 200,
      align: 'right',
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalNet || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalVat || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.totalPrices || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];
};

export const getTimesheetEmployeeColumns = ({
  messages,
  payrollCountry,
  handleCancel,
  handleOpenReverse,
  handleView,
  theme,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleCancel?: (list: TimeSheetDataItem[]) => void;
  handleOpenReverse?: (list: TimeSheetDataItem[]) => void;
  handleView?: (id: string) => void;
  theme: DefaultTheme;
}): ColumnsType<TimeSheetDataItem> => {
  return [
    getActionColumn({
      messages,
      handleCancel,
      handleOpenReverse,
      handleView,
      theme,
      hideCancel: true,
      hideReverse: true,
      width: 70,
    }),
    {
      key: 'approveStatus',
      title: messages['common.status'],
      align: 'center',
      fixed: 'left',
      dataIndex: 'approveStatus',
      render: (value) => (
        <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
          {formatStatus(value)}
        </AppTagStatus>
      ),
      width: 150,
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      dataIndex: 'id',
      width: 200,
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
      render: (value) => value?.batch || '_',
      width: 200,
    },
    {
      key: 'startDate',
      title: messages['timesheet.weekStartDate'],
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value?.startDate
          ? moment(value.startDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'endDate',
      title: messages['timesheet.weekEndDate'],
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value?.endDate
          ? moment(value.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
      width: 200,
    },
    {
      key: 'totalNet',
      title: messages['common.timesheetValue'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value?.totalNet, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
  ];
};
